import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
  BreadcrumbHeading,
  SoftButton,
  MultiSelect,
  Plus,
  PriceBandIcon,
  SearchField,
  Spinner,
  Typography,
  useTray,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useTimetables } from 'src/hooks/useTimetables';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { usePermissions } from 'src/hooks/usePermissions/usePermissions';
import { ITimetable } from 'src/api/getTimetables';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { TimetableCard } from 'src/components/TimetableCard';
import { CreateTimetableModal } from 'src/components/CreateTimetableModal';
import {
  DuplicateTimetableTrayContent,
  getNextOpenId,
  ICloseDuplicateTrayOptions,
} from 'src/components/DuplicateTimetableTray';
import { RenameTimetableTrayContent } from 'src/components/RenameTimetableTray';
import type { ITimetableLocationState } from 'src/pages/Timetable';
import styles from './Timetables.module.scss';

export const Timetables = () => {
  const navigate = useNavigate();
  const { trackEvent } = useUserTracking();
  const { openTray, closeTray, isTrayOpen } = useTray();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [selectedRangeIds, setSelectedRangeIds] = useState<string[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const rangeIds = selectedRangeIds.length > 0 ? selectedRangeIds : undefined;

  const { data: ranges } = useRanges();
  const rangeFilterOptions = useMemo(
    () => ranges?.map((range, index) => ({ id: range.range_id, label: range.name, display_order: index })) ?? [],
    [ranges],
  );
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();
  const { canCreateTimetables } = usePermissions();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useTimetables(
    15,
    debouncedSearchQuery,
    rangeIds,
  );

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage) fetchNextPage();
    },
  });

  useEffect(() => {
    document.title = 'Timetables';
    trackEvent('page_viewed', { page: 'timetables' });
  }, [trackEvent]);

  const timetables = data?.pages.flatMap((page) => page.data) ?? [];
  const totalCount = data?.pages[0]?.total_count ?? 0;
  const hasActiveFilters = !!debouncedSearchQuery || !!rangeIds;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const openDuplicateTray = (timetable: ITimetable) => {
    const onClose: (options?: ICloseDuplicateTrayOptions) => void = () => closeTray();

    openTray(
      <DuplicateTimetableTrayContent
        openId={getNextOpenId()}
        timetableId={timetable.timetable_id}
        sourceName={timetable.name}
        onClose={onClose}
      />,
      { title: 'Duplicate timetable' },
    );
  };

  const openRenameTray = (timetable: ITimetable) => {
    openTray(
      <RenameTimetableTrayContent
        openId={getNextOpenId()}
        timetableId={timetable.timetable_id}
        currentName={timetable.name}
        onClose={() => closeTray()}
      />,
      { title: 'Edit Name' },
    );
  };

  // The details tray previews its changes against the timetable's schedules, so it's opened on
  // the timetable page itself rather than here.
  const openTimetableDetails = (timetable: ITimetable) => {
    const state: ITimetableLocationState = { openDetailsTray: true };
    navigate(`${Routes.TIMETABLES}/${timetable.timetable_id}`, { state });
  };

  const getCountText = (): string => {
    if (isLoading) return 'Loading...';
    if (timetables.length > 0) return `${totalCount} ${pluralize('timetable', totalCount)}`;
    if (hasActiveFilters) return 'No results for your search...';
    return 'Build your first timetable...';
  };

  return (
    <div className={styles['page-container']}>
      <div className={styles['page-header']}>
        <BreadcrumbHeading breadcrumbs={[{ label: 'Timetables', path: Routes.TIMETABLES }]} />
        <div className={styles['filters']}>
          <SearchField placeholder="Search for a timetable" onChange={handleSearchChange} isDisabled={false} />
          {rangesEnabled && (
            <MultiSelect
              Icon={PriceBandIcon}
              label="Range"
              options={rangeFilterOptions}
              selected={selectedRangeIds}
              onChange={setSelectedRangeIds}
            />
          )}
        </div>
        <Typography>{getCountText()}</Typography>
      </div>
      <SoftButton
        colour="blueberry"
        icon={Plus}
        size="full-width"
        disabled={!canCreateTimetables || isTrayOpen}
        onClick={() => setIsCreateModalOpen(true)}
      >
        Create Timetable
      </SoftButton>
      <CreateTimetableModal open={isCreateModalOpen} closeModal={() => setIsCreateModalOpen(false)} />
      {(isLoading || timetables.length > 0) && (
        <div className={styles['list']}>
          {isLoading && <SkeletonContainer hiddenTitle />}
          {!isLoading &&
            timetables.map((timetable) => (
              <TimetableCard
                key={timetable.timetable_id}
                timetable={timetable}
                rangesEnabled={rangesEnabled}
                isTrayOpen={isTrayOpen}
                onDuplicate={openDuplicateTray}
                onEditDetails={openTimetableDetails}
                onRename={openRenameTray}
              />
            ))}
          {isFetchingNextPage && (
            <div className={styles['spinner-container']}>
              <Spinner size="medium" colour="rainbow" />
            </div>
          )}
          {hasNextPage && !isFetchingNextPage && <div ref={ref} />}
        </div>
      )}
    </div>
  );
};

export default Timetables;
